import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Heart, Menu, X, ChevronDown, MessageCircle, ShoppingBag } from "lucide-react";
import { NAV_LINKS, MEGA_MENU, BRAND, BRAND_FULL, waLink, WA_MESSAGES } from "@/config/site";
import { openBooking, fetchProducts, getWishlist } from "@/lib/api";
import { bagCount, openBag } from "@/lib/bag";

const SearchOverlay = ({ open, onClose }) => {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const navigate = useNavigate();
    const timer = useRef(null);

    useEffect(() => {
        if (!open) return;
        setQ("");
        setResults([]);
    }, [open]);

    useEffect(() => {
        clearTimeout(timer.current);
        if (!q.trim()) {
            setResults([]);
            return;
        }
        timer.current = setTimeout(() => {
            fetchProducts({ q, limit: 8 }).then((d) => setResults(d.items)).catch(() => {});
        }, 220);
        return () => clearTimeout(timer.current);
    }, [q]);

    const go = (path) => {
        onClose();
        navigate(path);
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[95] bg-obsidian/97 backdrop-blur-xl flex flex-col items-center pt-28 sm:pt-36 px-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="search-overlay"
                >
                    <button
                        onClick={onClose}
                        data-testid="search-close-btn"
                        aria-label="Close search"
                        className="absolute top-6 right-6 text-sand hover:text-gold transition-colors"
                    >
                        <X size={26} strokeWidth={1.25} />
                    </button>
                    <div className="w-full max-w-2xl">
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && q.trim() && go(`/collections?q=${encodeURIComponent(q)}`)}
                            placeholder="Search gold ring, bridal necklace…"
                            data-testid="search-input"
                            className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none font-serif text-3xl sm:text-5xl py-4 placeholder:text-ivory/25 text-ivory transition-colors"
                        />
                        {!q && (
                            <div className="flex flex-wrap gap-3 mt-8">
                                {["Gold ring", "Bridal necklace", "Diamond earrings", "Men's bracelet", "Engagement ring"].map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => setQ(s)}
                                        data-testid={`search-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className="text-xs tracking-[0.2em] uppercase text-sand border border-gold/20 px-4 py-2 hover:border-gold/60 hover:text-gold transition-colors"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
                            {results.map((p) => (
                                <button
                                    key={p.slug}
                                    onClick={() => go(`/product/${p.slug}`)}
                                    data-testid={`search-result-${p.slug}`}
                                    className="group text-left"
                                >
                                    <div className="aspect-[3/4] overflow-hidden border border-gold/10 group-hover:border-gold/50 transition-colors">
                                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                                    </div>
                                    <p className="mt-2 font-serif text-sm text-ivory group-hover:text-gold transition-colors">{p.name}</p>
                                    <p className="text-[10px] tracking-[0.25em] uppercase text-sand">{p.category}</p>
                                </button>
                            ))}
                            {q && results.length === 0 && (
                                <p className="col-span-full text-sand text-sm mt-4" data-testid="search-no-results">
                                    No pieces found — try "gold", "bridal" or "diamond".
                                </p>
                            )}
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [megaOpen, setMegaOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [wishCount, setWishCount] = useState(0);
    const [bagN, setBagN] = useState(0);
    const closeTimer = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 50);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        const syncWish = () => setWishCount(getWishlist().length);
        syncWish();
        window.addEventListener("bsk:wishlist", syncWish);
        const onSearch = () => setSearchOpen(true);
        window.addEventListener("bsk:search", onSearch);
        const syncBag = () => setBagN(bagCount());
        syncBag();
        window.addEventListener("bsk:bag", syncBag);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("bsk:wishlist", syncWish);
            window.removeEventListener("bsk:search", onSearch);
            window.removeEventListener("bsk:bag", syncBag);
        };
    }, []);

    const megaEnter = () => {
        clearTimeout(closeTimer.current);
        setMegaOpen(true);
    };
    const megaLeave = () => {
        closeTimer.current = setTimeout(() => setMegaOpen(false), 160);
    };

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 0.6, 0.24, 1] }}
                className={`fixed top-0 inset-x-0 z-[80] transition-all duration-500 ${
                    scrolled || mobileOpen
                        ? "bg-obsidian/85 backdrop-blur-md border-b border-gold/15 py-3"
                        : "bg-transparent py-5"
                }`}
                data-testid="main-navbar"
            >
                <div className="max-w-[1500px] mx-auto px-5 sm:px-8 flex items-center justify-between gap-4">
                    <Link to="/" className="shrink-0 group" data-testid="nav-logo">
                        <span className="font-serif text-xl sm:text-2xl tracking-[0.18em] text-ivory group-hover:text-gold transition-colors">
                            BSK <span className="gold-text">JEWELLERS</span>
                        </span>
                        <span className="block text-[9px] tracking-[0.3em] uppercase text-sand mt-0.5">{BRAND_FULL}</span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
                        {NAV_LINKS.map((l) =>
                            l.mega ? (
                                <div key={l.label} className="relative" onMouseEnter={megaEnter} onMouseLeave={megaLeave}>
                                    <Link
                                        to={l.to}
                                        data-testid="nav-collections"
                                        className="flex items-center gap-1 text-[11px] tracking-[0.22em] uppercase text-ivory/80 hover:text-gold transition-colors py-4"
                                    >
                                        {l.label} <ChevronDown size={12} className={`transition-transform duration-300 ${megaOpen ? "rotate-180" : ""}`} />
                                    </Link>
                                    <AnimatePresence>
                                        {megaOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: 12 }}
                                                transition={{ duration: 0.28, ease: "easeOut" }}
                                                className="absolute left-1/2 -translate-x-1/2 top-full w-[880px] max-w-[90vw] bg-charcoal/95 backdrop-blur-xl border border-gold/20 shadow-2xl shadow-black/60 p-8 grid grid-cols-6 gap-6"
                                                data-testid="collections-mega-menu"
                                            >
                                                {MEGA_MENU.map((col) => (
                                                    <div key={col.title}>
                                                        <p className="eyebrow mb-4 !text-[10px]">{col.title}</p>
                                                        <ul className="space-y-2.5">
                                                            {col.links.map((lnk) => (
                                                                <li key={lnk.label}>
                                                                    <Link
                                                                        to={lnk.to}
                                                                        onClick={() => setMegaOpen(false)}
                                                                        data-testid={`mega-${col.title.toLowerCase()}-${lnk.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                                                        className="text-sm text-sand hover:text-gold hover:pl-1 transition-all duration-300"
                                                                    >
                                                                        {lnk.label}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            ) : (
                                <Link
                                    key={l.label}
                                    to={l.to}
                                    data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="text-[11px] tracking-[0.22em] uppercase text-ivory/80 hover:text-gold transition-colors"
                                >
                                    {l.label}
                                </Link>
                            )
                        )}
                    </nav>

                    <div className="flex items-center gap-4 sm:gap-5">
                        <button onClick={() => setSearchOpen(true)} data-testid="nav-search-btn" aria-label="Search" className="text-ivory/80 hover:text-gold transition-colors">
                            <Search size={19} strokeWidth={1.5} />
                        </button>
                        <Link to="/wishlist" data-testid="nav-wishlist" aria-label="Wishlist" className="relative text-ivory/80 hover:text-gold transition-colors hidden sm:block">
                            <Heart size={19} strokeWidth={1.5} />
                            {wishCount > 0 && (
                                <span className="absolute -top-2 -right-2 bg-gold text-obsidian text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-medium" data-testid="wishlist-count">
                                    {wishCount}
                                </span>
                            )}
                        </Link>
                        <button onClick={openBag} data-testid="nav-bag" aria-label="Shopping bag" className="relative text-ivory/80 hover:text-gold transition-colors">
                            <ShoppingBag size={19} strokeWidth={1.5} />
                            {bagN > 0 && (
                                <span className="absolute -top-2 -right-2 bg-gold text-obsidian text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-medium" data-testid="bag-count">
                                    {bagN}
                                </span>
                            )}
                        </button>
                        <a
                            href={waLink(WA_MESSAGES.general)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="nav-whatsapp"
                            aria-label="WhatsApp"
                            className="text-ivory/80 hover:text-gold transition-colors"
                        >
                            <MessageCircle size={19} strokeWidth={1.5} />
                        </a>
                        <button
                            onClick={() => openBooking()}
                            data-testid="nav-book-appointment-btn"
                            className="hidden md:block text-[11px] tracking-[0.22em] uppercase border border-gold/50 text-gold px-5 py-2.5 hover:bg-gold hover:text-obsidian transition-colors duration-300"
                        >
                            Book Appointment
                        </button>
                        <button
                            onClick={() => setMobileOpen(!mobileOpen)}
                            data-testid="nav-mobile-toggle"
                            aria-label="Menu"
                            className="lg:hidden text-ivory"
                        >
                            {mobileOpen ? <X size={24} strokeWidth={1.25} /> : <Menu size={24} strokeWidth={1.25} />}
                        </button>
                    </div>
                </div>
            </motion.header>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[75] bg-obsidian pt-24 pb-28 px-8 overflow-y-auto lg:hidden"
                        data-testid="mobile-menu"
                    >
                        <nav className="flex flex-col gap-1" aria-label="Mobile">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div key={l.label} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * i }}>
                                    <Link
                                        to={l.to}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        className="block font-serif text-3xl py-3 text-ivory/90 hover:text-gold transition-colors border-b border-gold/10"
                                    >
                                        {l.label}
                                    </Link>
                                </motion.div>
                            ))}
                        </nav>
                        <div className="flex gap-4 mt-8">
                            <button
                                onClick={() => { setMobileOpen(false); openBooking(); }}
                                data-testid="mobile-book-appointment-btn"
                                className="flex-1 text-xs tracking-[0.2em] uppercase border border-gold/60 text-gold py-3.5"
                            >
                                Book Appointment
                            </button>
                            <a
                                href={waLink(WA_MESSAGES.general)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="mobile-whatsapp-btn"
                                className="flex-1 text-xs tracking-[0.2em] uppercase bg-gold text-obsidian py-3.5 text-center"
                            >
                                WhatsApp
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
        </>
    );
};

export default Navbar;
